import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  makeStyles,
  Button,
  IconButton,
  LinearProgress,
} from "@material-ui/core";
import { PendingReturn } from "../../../domain/PendingReturn";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import React, { Dispatch, useEffect, useState } from "react";
import { FileUploadDialog } from "../dialogs/FileUploadDialog";
import { CloudUpload } from "@material-ui/icons";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction } from "redux";
import {
  submitReturn,
  submitNilReturn,
  resetSubmission,
} from "../../../redux/actions";
import { submissionStateSelector } from "../../../redux/selectors";
import { ConfirmNilReturnDialog } from "../dialogs/ConfirmNilReturnDialog";

const useStyles = makeStyles((theme) => ({
  header: {
    fontWeight: "bold",
  },
}));

export const CollectionList: React.FC<{
  items: PendingReturn[];
  loading: any;
  setLoading: Dispatch<any>;
  loaded: any;
  setLoaded: Dispatch<any>;
}> = ({ items, loading, setLoading, loaded, setLoaded }) => {
  const classes = useStyles();
  const [isDialogOpen, setIsOpenUploadDialog] = useState(false);
  const [isNilReturnOpen, setIsNilReturnOpen] = useState(false);
  const [file, setFile] = useState<File | undefined>();
  const [currentFileKey, setCurrentFileKey] = useState<string>("");
  const [templateName, setTemplateName] = useState<string>("");
  const [alertState, setAlertState] = useState<boolean>(false);
  const [caoId, setCaoId] = useState<number | null>(null);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const submissionState = useSelector(submissionStateSelector);
  const [nilReturnTemplateId, setNilReturnTemplateId] = useState<number | null>(null);
  const [nilReturnCaoid, setNilReturnCaoid] = useState<number | null>(null);
  const [nilReturnKey, setNilReturnKey] = useState<string | null>(null);

  const dispatch = useDispatch();

  const onSubmit = (
    data: any,
    key: string,
    templateName: string,
    collectionAoId: number,
    templateId: number
  ) => {
    setCurrentFileKey(key);
    setTemplateName(templateName);
    let file = data as File[];
    setCaoId(collectionAoId);
    setTemplateId(templateId);
    if (file === null) {
      return;
    }
    setFile(file[0]);
    setIsOpenUploadDialog(true);
  };

  const onSubmitNilReturnClick = (
    key: string,
    collectionAoId: number,
    templateId: number
  ) => {
    setNilReturnKey(key);
    setNilReturnCaoid(collectionAoId);
    setNilReturnTemplateId(templateId);
    setIsNilReturnOpen(true);
  }

  const onSubmitNilReturn = () => {
    if (nilReturnCaoid && nilReturnTemplateId && nilReturnKey) {
      dispatch(submitNilReturn(nilReturnCaoid, nilReturnTemplateId));
      setLoaded((prevState: any) => {
        return { ...prevState, [nilReturnKey]: true };
      });
      onCloseNilReturnDialog();
    } else {
      console.error(`State incorrectly set for submitting nil return: KEY ${nilReturnKey} | CAOID ${nilReturnCaoid} | TEMPLATE ${nilReturnTemplateId}`);
    }
  };

  const onCloseNilReturnDialog = () => {
    setNilReturnKey(null);
    setNilReturnCaoid(null);
    setNilReturnTemplateId(null);
    setIsNilReturnOpen(false);
  }

  const onRetrySubmission = (key: string) => dispatch(resetSubmission(key));

  useEffect(() => {
    if (loading) {
      for (const loadingItem in loading) {
        if (loading[loadingItem] < 100 && loading[loadingItem] !== undefined) {
          setAlertState(true);
          if (alertState) {
            window.onbeforeunload = function () {
              return "An item is still being uploaded. Are you sure you want to navigate away from this page and lose these changes?";
            };
          }
        } else {
          setAlertState(false);
          if (alertState) {
            //unregister alert if 100%
            window.onbeforeunload = function () {};
          }
        }
      }
    }
  }, [loading, alertState]);

  const dataAvailable = !!loading && !!loaded;
  const isLoading = (key: string) =>
    dataAvailable &&
    Object.keys(loading).includes(key) &&
    !Object.keys(loaded).includes(key);

  const renderStatusCells = (item: PendingReturn) => {
    const key = `${item.collectionAwardingOrganisationId}-${item.templateId}`;
    const submission = submissionState?.submissions?.find((x) => x.key === key);

    if (!!submission) {
      const isSuccess = submission.status === "SUCCESS";

      if (submission.status === "UPLOADED") {
        return (
          <React.Fragment key={key}>
            <TableCell colSpan={5}>Uploaded - Pending validation</TableCell>
            <TableCell>
              <CheckCircleIcon
                style={{ color: "green" }}
                data-test="submissionSuccessIcon"
              />
            </TableCell>
          </React.Fragment>
        );
      }

      if (submission.status === "NIL_RETURN_PENDING") {
        return (
          <React.Fragment key={key}>
            <TableCell colSpan={5}>Pending validation</TableCell>
            <TableCell>
              <CheckCircleIcon
                style={{ color: "green" }}
                data-test="submissionSuccessIcon"
              />
            </TableCell>
          </React.Fragment>
        );
      }

      // todo: check if we need to surface the error
      return (
        <React.Fragment key={key}>
          <TableCell colSpan={isSuccess ? 5 : 4} data-test="statusValue">
            {isSuccess ? "Submitted" : "Failed"}
            <br />
            {submission.message}
          </TableCell>
          {isSuccess ? (
            <TableCell>
              <CheckCircleIcon
                style={{ color: "green" }}
                data-test="nilReturnSuccessIcon"
              />
            </TableCell>
          ) : (
            <TableCell>
              <Button
                variant="outlined"
                onClick={() => onRetrySubmission(key)}
                data-test="retryButton"
              >
                Retry
              </Button>
            </TableCell>
          )}
        </React.Fragment>
      );
    }
    if (isLoading(key)) {
      return (
        <React.Fragment key={key}>
          <TableCell colSpan={12}>
            <LinearProgress
              variant="determinate"
              value={
                loading[
                  `${item.collectionAwardingOrganisationId}-${item.templateId}`
                ]
              }
            />
          </TableCell>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={key}>
        <TableCell scope="row">{item.status}</TableCell>
        {(item.uploadFlag === true) ? (
          <>
            <TableCell>
              <IconButton component="label">
                <CloudUpload />
                <input
                  type="file"
                  hidden
                  onChange={(e: any) => {
                    onSubmit(
                      e.target.files,
                      `${item.collectionAwardingOrganisationId}-${item.templateId}`,
                      item.uploadFolderName,
                      item.collectionAwardingOrganisationId,
                      item.templateId
                    );
                  }}
                  data-test="uploadButton"
                />
              </IconButton>
            </TableCell>
            <TableCell>
              <Button
                variant="outlined"
                onClick={() =>
                  onSubmitNilReturnClick(
                    `${item.collectionAwardingOrganisationId}-${item.templateId}`,
                    item.collectionAwardingOrganisationId,
                    item.templateId
                  )
                }
                data-test="nilReturnButton"
              >
                No data to submit
              </Button>
            </TableCell>
            <TableCell></TableCell>
          </>
        ) : (
          <TableCell colSpan={3}></TableCell>
        )}
      </React.Fragment>
    );
  };
  
  const sortByCollectionStartDate = (a: PendingReturn, b: PendingReturn) => 
    new Date(a.collectionStartDate).getTime() - new Date(b.collectionStartDate).getTime();

  return (
    <>
      {items.length > 0 ? (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.header}>
                    Reporting Period
                  </TableCell>
                  <TableCell className={classes.header}>Collection</TableCell>
                  <TableCell className={classes.header}>Due Date</TableCell>
                  <TableCell colSpan={12} className={classes.header}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items?.sort(sortByCollectionStartDate)?.map((item: PendingReturn, index) => (
                  <TableRow
                    key={`${item.collectionAwardingOrganisationId}-${item.templateId}`}
                  >
                    <TableCell scope="row">{item.regulatorReference}</TableCell>
                    <TableCell scope="row">
                      {item.collectionName}
                      <br />
                      <Typography variant="subtitle2">
                        <strong>Template name:</strong> {item.templateName}
                      </Typography>
                    </TableCell>
                    <TableCell scope="row">
                      {formatUtcDate(
                        item.collectionDueDate,
                        standardDateFormats.DATE
                      )}
                    </TableCell>
                    {renderStatusCells(item)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <FileUploadDialog
            index={currentFileKey}
            setLoading={setLoading}
            setLoaded={setLoaded}
            templateName={templateName}
            templateId={templateId}
            collectionAoId={caoId}
            file={file}
            onClose={() => {
              setFile(undefined);
              setIsOpenUploadDialog(false);
            }}
            open={file !== null && isDialogOpen}
          />
          <ConfirmNilReturnDialog
            open={isNilReturnOpen}
            onClose={() => onCloseNilReturnDialog()}
            onSubmit={() => onSubmitNilReturn()}
          />
        </>
      ) : (
        <Typography variant="h6">No data</Typography>
      )}
    </>
  );
};
